// Standard library imports
import React from 'react';
// External library imports
import _ from 'lodash';
// Internal module imports
import bidClasses from '../../Bidding.module.css';
import { getColumnWithSlotSize } from '../../../../../utils/biddingHelper';
import { momentTimeFormater } from '../../../../../utils/timeHelper';
import Typography from '../../../../../components/Typography/Typography';
import CustomTooltip from '../../../../../components/CustomToolTip/CustomTooltip';
import { Capitalize } from '../../../../../utils/stringHelper';

const getOrdinal = (n) => {
    const s = ['th', 'st', 'nd', 'rd'];
    const v = n % 100;
    return `${n}${s[(v - 20) % 10] || s[v] || s[0]}`;
};

const getEffectiveFromTooltip = (entry) => {
    const status = _.get(entry, 'status', '');
    const previousStatus = _.get(entry, 'previousStatus', '');
    const minute = _.get(entry, 'minute');
    if (!status) return '';
    const base = `${Capitalize(status)} from ${getOrdinal(minute)} minute`;
    return previousStatus ? `${base} (previously ${Capitalize(previousStatus)})` : base;
};

const Price = ({ data }) => {
    const length = _.get(data, ['overall'], []).length || 24;
    const slots = Array.from({ length: length }, (__, i) => i);
    const columns = getColumnWithSlotSize(slots, 8);
    const isEnergyPrice = _.get(data, 'regulationPrice', []).length ? true : false;
    const energyRequestBidStatus = _.get(data, 'energyRequestBidStatus', []);
    const energyStatusEffectiveFrom = _.get(data, 'energyStatusEffectiveFrom', []);
    const bidDate = data.bidDate;

    return (
        <div className={`${bidClasses.InfoModalContainer}`}>
            {!isEnergyPrice ? (
                <div>
                    <div className={bidClasses.InfoModalHeading}>
                        <Typography content={'Overall Bid Price'} size={14} />
                    </div>
                    <div className={bidClasses.TableContainer}>
                        {columns.map((column, colIndex) => (
                            <table key={colIndex}>
                                <thead>
                                    <tr>
                                        <th>Slot</th>
                                        <th>Hour</th>
                                        <th>Cost</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {column.map((key) => (
                                        <tr key={key}>
                                            <td>{key + 1}</td>
                                            <td>
                                                {momentTimeFormater(bidDate).add(key, 'hour').hour()}-
                                                {momentTimeFormater(bidDate)
                                                    .add(key + 1, 'hour')
                                                    .hour()}
                                            </td>
                                            <td>{_.get(data, ['overall', key])}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        ))}
                    </div>
                </div>
            ) : (
                ''
            )}
            {_.get(data, 'primary', []).length ? (
                <div style={{ marginTop: '3vh' }}>
                    <div className={bidClasses.InfoModalHeading}>
                        <Typography content={isEnergyPrice ? 'Capacity Bid Price' : 'Primary Bid Price'} size={14} />
                    </div>

                    <div className={bidClasses.TableContainer}>
                        {columns.map((column, colIndex) => (
                            <table key={colIndex}>
                                <thead>
                                    <tr>
                                        <th>Slot</th>
                                        <th>Hour</th>
                                        <th>Cost</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {column.map((key) => (
                                        <tr key={key}>
                                            <td>{key + 1}</td>
                                            <td>
                                                {momentTimeFormater(bidDate).add(key, 'hour').hour()}-
                                                {momentTimeFormater(bidDate)
                                                    .add(key + 1, 'hour')
                                                    .hour()}
                                            </td>
                                            <td>{_.get(data, ['primary', key])}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        ))}
                    </div>
                </div>
            ) : (
                ''
            )}
            {isEnergyPrice ? (
                <div style={{ marginTop: '3vh' }}>
                    <div className={bidClasses.InfoModalHeading}>
                        <Typography content={'Energy Bid Price'} size={14} />
                    </div>

                    <div className={bidClasses.TableContainer}>
                        {columns.map((column, colIndex) => (
                            <table key={colIndex}>
                                <thead>
                                    <tr>
                                        <th>Slot</th>
                                        <th>Hour</th>
                                        <th>Cost</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {column.map((key) => {
                                        const status = _.get(energyRequestBidStatus, [key]);
                                        const isInactive = status === 'inactive';
                                        const isNotPosted = status !== 'inactive' && status !== 'active';
                                        const effectiveFromEntry = _.find(energyStatusEffectiveFrom, ['hour', key]);
                                        const rowClassName = isInactive ? 'orangeColor' : isNotPosted ? 'redColor' : undefined;

                                        const hourRangeText = (
                                            <>
                                                {momentTimeFormater(bidDate).add(key, 'hour').hour()}-
                                                {momentTimeFormater(bidDate)
                                                    .add(key + 1, 'hour')
                                                    .hour()}
                                            </>
                                        );
                                        const hourCell = isNotPosted ? (
                                            <CustomTooltip content={'Not posted'}>
                                                <span>
                                                    {hourRangeText}
                                                    <span className="required"> *</span>
                                                </span>
                                            </CustomTooltip>
                                        ) : effectiveFromEntry ? (
                                            <CustomTooltip content={getEffectiveFromTooltip(effectiveFromEntry)}>
                                                <span>
                                                    {hourRangeText}
                                                    <span className="required"> *</span>
                                                </span>
                                            </CustomTooltip>
                                        ) : (
                                            hourRangeText
                                        );

                                        return (
                                            <tr key={key}>
                                                <td className={rowClassName}>{key + 1}</td>
                                                <td className={rowClassName}>{hourCell}</td>
                                                <td className={rowClassName}>{_.get(data, ['regulationPrice', key])}</td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        ))}
                    </div>
                </div>
            ) : (
                ''
            )}
            {_.get(data, 'secondary', []).length ? (
                <div style={{ marginTop: '3vh' }}>
                    <div className={bidClasses.InfoModalHeading}>
                        <Typography content={'Secondary Bid Price'} size={14} />
                    </div>
                    <div className={bidClasses.TableContainer}>
                        {columns.map((column, colIndex) => (
                            <table key={colIndex}>
                                <thead>
                                    <tr>
                                        <th>Slot</th>
                                        <th>Hour</th>
                                        <th>Cost</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {column.map((key) => (
                                        <tr key={key}>
                                            <td>{key + 1}</td>
                                            <td>
                                                {momentTimeFormater(bidDate).add(key, 'hour').hour()}-
                                                {momentTimeFormater(bidDate)
                                                    .add(key + 1, 'hour')
                                                    .hour()}
                                            </td>
                                            <td>{_.get(data, ['secondary', key])}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        ))}
                    </div>
                </div>
            ) : (
                ''
            )}
        </div>
    );
};

export default Price;
